import { ServiceId } from '@jarvis/jweb-core'
import DeviceResource from '@jarvis/react-device-resources-provider'

/**
 * Data received through _DeviceSelectedForOobe_ event
 */
export interface DeviceSelectedForOobeDataInterface {
  /**
   * Connectivity between device and app. Required if onboardingContext.entry is "appOobe".
   *
   * example: network
   */
  Connectivity: ConnectivityToApp
  /**
   * Onboarding session ID
   */
  SessionId: string
}

export interface SessionPayload {
  onboardingContext: OnboardingContext
  app: SessionCreationAppAttributes
  device?: SessionCreationDeviceAttributes
}

/**
 * Payload for creating a new onboarding session
 */
export interface OnboardingPlan {
  sessionContext?: SessionContext
  nextService?: NextService
}
export interface SessionContext {
  /**
   * Onboarding session ID
   */
  sessionId: string
  /**
   * Service Id of the Onboarding Stage currently being executed
   */
  currentServiceId?: string
  /**
   * Recommended value for the X-Correlation-ID header to be used by each Onboarding Stage
   * when making external API calls, if the API supports the X-Correlation-ID header.
   *
   * example: _[OD sessionId]-[Onboarding Stage serviceId]_
   */
  xCorrelationId?: string
  onboardingContext: OnboardingContext
  app: SessionContextAppAttributes | SessionCreationAppAttributes
  device?: SessionContextDeviceAttributes | SessionCreationDeviceAttributes
  user?: User
  tenant?: Tenant
  derivedAttributes?: DerivedAttributes
  /**
   * Array of step data used to render the Stepper UI
   */
  stepperState?: Step[]
}

/**
 * Response of creating a new onboarding session
 */
export interface CreateSessionResponse {
  sessionContext: SessionContext
  nextService?: NextService
  event?: string
}
export interface User {
  /**
   * Initial authentication state of the user, based on the bearer token passed in the Create request
   */
  initialAuthenticationState?: AuthenticationState
  /**
   * Current authentication state of the user, based on the bearer token passed in the most recent Update request
   */
  currentAuthenticationState?: AuthenticationState
  /**
   * array of JSON objects representing the user's consents
   */
  consents?: Consent[]
}

/**
 * Tenant information
 */
export interface Tenant {
  /**
   * Account(Tenant) Resource ID
   */
  resourceId?: string
  /**
   * The name of Account(Tenant)
   */
  accountName?: string
  /**
   * The type of Account(Tenant)
   */
  type?: string
}

/**
 * Attributes derived by OD calling the Stratus PLS API, when product number is available
 */
export interface DerivedAttributes {
  pls?: Pls
}

/**
 * Attributes derived by OD calling the Stratus PLS API, when product number is available
 */
export interface Pls {
  /**
   * onboardingCapabilities returned in the response from the Stratus PLS API, if available
   */
  onboardingCapabilities?: Record<string, unknown>
}

/**
 * Step data used to render the Stepper UI
 */
export interface Step {
  /**
   * Used by the UI to map to a localized label
   */
  stepId: StepId
  /**
   * Status of the step
   */
  status: StepState
  /**
   * Placement of the item on the step sequence
   */
  order: number
}

/**
 * JSON object representing the user consent
 */
export interface Consent {
  /**
   * purposeId associated with the consent
   */
  purposeId: string
  /**
   * user's opt-in status for the given purposeId
   */
  state: string
}

export interface NextService {
  /**
   * Service Id for the Onboarding Agent to execute
   */
  serviceId: ServiceId
  /**
   * URL to UPDATE SESSION API, used to report the results of Service and to receive the next service to execute
   *
   * example: https://.../path/to/update/session
   */
  resultUrl: string
  /**
   * URL or deep-link to which Onboarding Agent should redirect when the onboarding process finishes
   *
   * example: https://.../path/to/redirect/after/onboarding/finishes
   */
  exitUrl?: string
}

export interface OnboardingContext {
  /**
   * Experience type
   */
  experience?: ExperienceType
  /**
   * How the user entered into this experience
   */
  entry: Entry
  /**
   * bizModel (E2E, Flex) inferred, for example, through a vanity URL
   *
   * example: E2E
   */
  bizModelHint?: string
  /**
   * Full URL (including path and query string) that initiated the onboarding process, if available
   *
   * example: https://admin.hpsmart.com/activate?earlyOobe=easystartwin&printerUuid=abc
   */
  entryUrl?: string
  /**
   * The offer that the printer is eligible for, inferred, for example, through a vanity URL.
   */
  offerHint?: string
}

/**
 * App attributes required for the session creation
 */
export interface SessionCreationAppAttributes {
  /**
   * clientId for this app, as registered with Onboarding Director
   *
   * example: fdaa06d9-1d5d-4604-91ae-ff886879d36d
   */
  clientId: string
  /**
   * Version of the setup app that is hosting the Onboarding Agent, if applicable
   *
   * example: HP Smart Windows 129.0.3914.0
   */
  appVersion?: string
  /**
   * Operating System
   *
   * cases: ios, android, macos, windows
   */
  osType: string
  /**
   * Operating System version
   *
   * example: 10.0.17763.1
   */
  osVersion: string
  /**
   * country and region codes as per ISO 3166-1 alpha-2
   *
   * example: US
   */
  countryRegionIso?: string
  /**
   * The combination of user’s language and geographic region,
   * represented as {language-code}_{country-code}, per ISO 3166 and ISO 639, respectively.
   *
   * example: en_US
   */
  locale?: string
  /**
   * Type of container
   *
   * example: jarvisApp
   */
  container: string
  /**
   * Application instance ID
   *
   * example: 12345678-1234-1234-1234-123456789012
   */
  appInstanceId?: string
}

/**
 * Device attributes required for the session creation.
 */
export interface SessionCreationDeviceAttributes {
  /**
   * Connectivity between device and app. Required if onboardingContext.entry is "appOobe".
   *
   * example: network
   */
  connectivityToApp?: ConnectivityToApp
  /**
   * LEDM/CDM resource retrieved from the device.
   * Required if onboardingContext.entry is "appOobe".
   */
  deviceResources?: DeviceResource.DeviceResource[]
  /**
   * bizModel (E2E, Flex) configured in the device, as returned from a Stratus API such as DAG, UCDE, or Device
   */
  bizModel?: string
  /**
   * offer configured in the device, as returned from a Stratus API such as DAG, UCDE, or Device
   */
  offer?: number
}

/**
 *  Context to the Device Attributes
 */
export interface SessionContextDeviceAttributes {
  /**
   * uuid returned in the response from the DAG User Code Validation API
   */
  uuid?: string
  /**
   * productNumber returned in the response from the DAG User Code Validation API
   */
  productNumber?: string
  /**
   * serialNumber returned in the response from the DAG User Code Validation API
   */
  serialNumber?: string
  /**
   * modelName returned in the response from the DAG User Code Validation API
   */
  modelName?: string
  /**
   * bizModel returned in the response from the DAG User Code Validation API
   */
  bizModel?: string
  /**
   * cloudId returned in the response from the UCDE Register Device API
   */
  cloudId?: string
  /**
   * deviceLocation provided in the request to the Device Shadow Device Configuration API
   */
  deviceLocation?: string
  /**
   * deviceName provided in the request to the Device Shadow Device Configuration API
   */
  deviceName?: string
  /**
   * URL to the highest resolution image as derived by the onbp-jwv-printer-activation stage from the images array
   */
  highestResImgUrl?: string
  /**
   * subset of the data provided in the response from the Product Lookup Service API (for example, images or deviceCapabilities) that may be used by other Onboarding Stages.
   */
  pls?: Record<string, unknown>
}

/**
 * Context to the App Attributes.
 */
export interface SessionContextAppAttributes {
  /**
   * Version of the setup app that is hosting the Onboarding Agent, if applicable
   * Ex: HP Smart Windows 129.0.3914.0
   */
  appVersion?: string
  /**
   * Operating System
   */
  osType?: OsType
  /**
   * Operating System version
   * Ex: 10.0.17763.1
   */
  osVersion?: string
  /**
   * Country and region codes as per ISO 3166-1 alpha-2
   * Ex: US
   */
  countryRegionIso?: string
  /**
   * The combination of user’s language and geographic region, represented as {language-code}_{country-code}, per ISO 3166 and ISO 639, respectively.
   * Ex: en_GB, zh-Hans_HK, zh_HK.
   */
  locale?: string
  /**
   * Type of container
   */
  container?: Container
  /**
   * Inputs required by the ExitOOBE Onboarding Stage
   */
  exitOobe?: ExitOobe
}

/**
 * Inputs required by the ExitOOBE Onboarding Stage
 */
export interface ExitOobe {
  /**
   * URL to PDF used in the First Print activity
   */
  firstPrintPdfUrl?: string
  /**
   * List of activities to execute during the ExitOOBE Onboarding Stage
   */
  activities?: ExitOobeActivity[]
}

/**
 * Activity to execute during the ExitOOBE Onboarding Stage
 */
export interface ExitOobeActivity {
  /**
   * Name of ExitOOBE activity
   */
  name: ExitOobeActivityName
  /**
   * Whether or not to execute the activity
   */
  enabled: boolean
  /**
   * The suggested order in which to execute this activity, relative to other activities within the ExitOOBE Onboarding Stage. Client can ignore and change the order if needed. Omitted if enabled is \false.
   */
  suggestedOrder?: number
}

export interface ExecutionError {
  gun: string
  errorDetail: string
}
export interface ResourceError {
  errorCode: string
  executionErrors?: ExecutionError[]
}

/**
 * Name of ExitOOBE activity
 */
export enum ExitOobeActivityName {
  FIRST_PRINT = 'firstPrint',
  PRINT_FROM_OTHER_DEVICES = 'printFromOtherDevices'
}

/**
 * Connectivity between device and app. Required if onboardingContext.entry is "appOobe".
 */
export enum ConnectivityToApp {
  NETWORK = 'network',
  USB = 'usb',
  WIFI_DIRECT = 'wifidirect',
  NOT_CONNECTED = 'notconnected'
}

/**
 * Operating System
 */
export enum OsType {
  WINDOWS = 'windows',
  MACOS = 'macos',
  ANDROID = 'android',
  IOS = 'ios'
}

/**
 * Type of container
 */
export enum Container {
  BROWSER = 'browser',
  JARVIS_APP = 'jarvisApp',
  NON_JARVIS_APP = 'nonJarvisApp'
}

/**
 * Used by the UI to map to a localized label
 */
export enum StepId {
  ONBP_STEP_ASSIGN_OWNERSHIP = 'onbp-step-assign-ownership',
  ONBP_STEP_ENTER_PAIRING_CODE = 'onbp-step-enter-pairing-code',
  ONBP_STEP_INSTANT_INK = 'onbp-step-instant-ink',
  ONBP_STEP_CONFIGURE_AND_TEST = 'onbp-step-configure-and-test'
}

/**
 * Status of the step
 */
export enum StepState {
  complete = 'complete',
  current = 'current',
  incomplete = 'incomplete',
  disabled = 'disabled'
}

/**
 * How the user entered into this experience
 */
export enum Entry {
  PORTAL_OOBE = 'portalOobe',
  EARLY_OOBE = 'earlyOobe',
  APP_OOBE = 'appOobe',
  HPX_OOBE = 'hpxOobe',
  PORTAL_ENROLL = 'portalEnroll',
  PORTAL_SCAN_SETUP = 'portalScanSetup',
  JSHELL_TEST = 'jshellTest'
}

/**
 * Authentication state of the user
 */
export enum AuthenticationState {
  NOT_AUTHENTICATED = 'notAuthenticated',
  AUTHENTICATED = 'authenticated'
}

/**
 * Experience type
 */
export enum ExperienceType {
  SMB = 'smb',
  PC = 'pc',
  ECP = 'ecp',
  DEVICE_SELECTED_FOR_OOBE = 'DeviceSelectedForOobe',
  DEVICE_SELECTED_TO_ADD_TO_CAROUSEL = 'DeviceSelectedToAddToCarousel',
  DEVICE_SELECTED_FOR_POST_OOBE_PRINTER_CLAIM = 'DeviceSelectedForPostOobePrinterClaim'
}

export interface PLSData {
  /**
   * onboardingCapabilities returned in the response from the Stratus PLS API, if available
   */
  onboardingCapabilities?: OnboardingCapabilities
}

/**
 * onboardingCapabilities returned in the response from the Stratus PLS API, if available
 */
export interface OnboardingCapabilities {
  prohibitedClients?: string[]
  allowedClients?: string[]
}
